/**
 * StagedSendModal — plan a large send as a series of waves. Splits a STATIC
 * source list into disjoint, wave-sized static lists so a big campaign (e.g. 1M+)
 * can be sent over days, one wave at a time, evaluating deliverability between
 * waves. Each wave becomes a normal static list the operator then sends as its
 * own flight (with G3 grading, the sender-domain choice, scheduling, and the
 * bounce/deferral dashboards to gate the next wave).
 */
import { useEffect, useState } from "react";
import ListAutocomplete from "../common/ListAutocomplete";
import { getListById } from "../../services/lists.service";
import {
  planStagedSend,
  type StagedSendResult,
} from "../../services/stagedSend.service";
import { computeWaveSizes } from "../../utils/stagedSendWaves";
import type { List } from "../../types/list.types";

interface StagedSendModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const StagedSendModal: React.FC<StagedSendModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [sourceListId, setSourceListId] = useState("");
  const [source, setSource] = useState<List | null>(null);
  const [loadingSource, setLoadingSource] = useState(false);
  const [label, setLabel] = useState("");
  const [firstWave, setFirstWave] = useState("100000");
  const [subsequentWave, setSubsequentWave] = useState("150000");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<StagedSendResult | null>(null);

  useEffect(() => {
    if (!sourceListId) {
      setSource(null);
      return;
    }
    let cancelled = false;
    setLoadingSource(true);
    setError(null);
    (async () => {
      try {
        const l = await getListById(sourceListId);
        if (cancelled) {
          return;
        }
        setSource(l);
        // Default the label + reset any prior result when the source changes.
        setResult(null);
        if (l && !label.trim()) {
          setLabel(`${l.name} — Staged Send`);
        }
      } catch {
        if (!cancelled) {
          setSource(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingSource(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceListId]);

  if (!isOpen) {
    return null;
  }

  const total = source?.contact_count ?? 0;
  const isDynamic = source?.is_dynamic === true;
  const previewWaves = computeWaveSizes(
    total,
    parseInt(firstWave, 10),
    parseInt(subsequentWave, 10),
  );

  // computeWaveSizes caps at 50 waves. If the chosen sizes need more than that,
  // the returned plan sums to LESS than the source — it can't cover everyone, and
  // the server would reject it ("too many waves"). Detect that here so we neither
  // claim full coverage nor let the operator submit an impossible plan.
  const covered = previewWaves.reduce((a, b) => a + b, 0);
  const truncated = total > 0 && covered < total;
  const minWaveSize = total > 0 ? Math.ceil(total / 50) : 0;

  const canCreate =
    !!source &&
    !isDynamic &&
    total > 0 &&
    previewWaves.length > 0 &&
    !truncated &&
    label.trim() !== "" &&
    !creating;

  const handleCreate = async () => {
    if (!canCreate) {
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await planStagedSend(
        sourceListId,
        previewWaves,
        label.trim(),
      );
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            Plan a staged send
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {result ? (
          /* ---- Result ---------------------------------------------------- */
          <div className="px-6 py-4 space-y-4">
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
              Created <strong>{result.waves.length}</strong> wave lists covering{" "}
              <strong>{result.total.toLocaleString()}</strong> contacts. They're
              regular static lists — send each one as its own flight.
            </div>
            <div className="border rounded-lg divide-y">
              {result.waves.map((w) => (
                <div
                  key={w.list_id}
                  className="flex justify-between px-3 py-2 text-sm"
                >
                  <span className="text-gray-700">Wave {w.wave_index}</span>
                  <span className="font-medium text-gray-900">
                    {w.contact_count.toLocaleString()} contacts
                  </span>
                </div>
              ))}
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800">
              <p className="font-medium mb-1">Next steps</p>
              <p>
                Create a flight for <strong>Wave 1</strong> (find it in the
                segment picker by this label), send it, and watch its
                delivered/bounced/throttled numbers. Only when it looks healthy,
                send Wave 2 — and so on. Staging the waves is what protects
                deliverability; the From-domain selector does not change the
                sending IP, so don't rely on it to spread load.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* ---- Planner --------------------------------------------------- */
          <div className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source list (static)
              </label>
              <ListAutocomplete
                value={sourceListId}
                onChange={setSourceListId}
                staticOnly
              />
              <p className="mt-1 text-xs text-gray-500">
                The full audience to split. Must be a static list so the waves
                are a fixed, deterministic partition (dynamic lists change over
                time).
              </p>
              {loadingSource && (
                <p className="mt-1 text-xs text-gray-400">Loading list…</p>
              )}
              {source && !isDynamic && (
                <p className="mt-1 text-xs text-gray-600">
                  <strong>{total.toLocaleString()}</strong> contacts in this
                  list.
                </p>
              )}
              {source && isDynamic && (
                <p className="mt-1 text-xs text-red-600">
                  This is a dynamic list — pick a static list instead.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. August Member Blast — Staged Send"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Each wave list is named "{label || "…"} — Wave N/…".
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First wave size
                </label>
                <input
                  type="number"
                  min={1}
                  value={firstWave}
                  onChange={(e) => setFirstWave(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Send this many first, then evaluate.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Each following wave
                </label>
                <input
                  type="number"
                  min={1}
                  value={subsequentWave}
                  onChange={(e) => setSubsequentWave(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Size of each later wave; the last holds the remainder.
                </p>
              </div>
            </div>

            {previewWaves.length > 0 && (
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Plan: {previewWaves.length} wave
                  {previewWaves.length === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-gray-600">
                  {previewWaves
                    .map((w, i) => `Wave ${i + 1}: ${w.toLocaleString()}`)
                    .join("  ·  ")}
                </p>
                {truncated ? (
                  <p className="mt-1 text-xs text-red-600">
                    These {previewWaves.length} waves cover only{" "}
                    {covered.toLocaleString()} of {total.toLocaleString()}{" "}
                    contacts — a plan is capped at 50 waves. Increase the wave
                    size to at least {minWaveSize.toLocaleString()} so every
                    contact fits.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Total {covered.toLocaleString()} — every contact lands in
                    exactly one wave.
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={!canCreate}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating
                  ? "Creating waves…"
                  : `Create ${previewWaves.length || ""} wave lists`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StagedSendModal;
